import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ButtonBase,
  Box,
  useTheme,
} from '@mui/material';

import { Flex } from '../utils/Flex.component';

import {
  usePowerNavigationFeed,
  INavigationItem,
} from '@/components/PowerUser/PowerUserNavigation.feed';

export const PowerUserNavigation = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listItemRefs = useRef<(HTMLLIElement | null)[]>([]);

  const navigationFeed = usePowerNavigationFeed();

  const handleOpen = () => {
    setOpen(true);
    setFilterText('');
    setSelectedIndex(0);
  };

  // Global keydown handler for (ctrl/cmd)+k to toggle the dialog
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Check if ctrl or cmd is pressed along with "k"
      if ((e.ctrlKey || e.metaKey) && e.key?.toLowerCase() === 'k') {
        e.preventDefault();
        // Toggle dialog open state
        setOpen((prevOpen) => {
          if (!prevOpen) {
            // When opening, reset filter and selection
            setFilterText('');
            setSelectedIndex(0);
          }
          return !prevOpen;
        });
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, []);

  // Filter routes based on the input text.
  const filteredItems = useMemo(() => {
    const lowerFilter = filterText.toLowerCase();

    return navigationFeed
      .filter((item: INavigationItem) => {
        return (
          item.name.toLowerCase().includes(lowerFilter) ||
          item.description.toLowerCase().includes(lowerFilter) ||
          item.path.toLowerCase().includes(lowerFilter)
        );
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [filterText, navigationFeed]);

  // Ensure the selected index is within bounds whenever the filteredRoutes change.
  useEffect(() => {
    if (filteredItems.length === 0) {
      setSelectedIndex(0);
    } else if (selectedIndex >= filteredItems.length) {
      setSelectedIndex(0);
    }
  }, [filteredItems, selectedIndex]);

  // Closes the dialog.
  const handleClose = () => {
    setOpen(false);
  };

  // Redirects to the given route and closes the dialog.
  const handleRouteSelect = (item: INavigationItem) => {
    if (item.blank) {
      window.open(item.path, '_blank');
    } else {
      navigate(item.path);
    }
    setOpen(false);
  };

  // Handle key events on the search input.
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (filteredItems.length === 0) {
      return;
    }

    if (e.key === 'ArrowDown' || (e.shiftKey && e.key === 'J')) {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % filteredItems.length);
    } else if (e.key === 'ArrowUp' || (e.shiftKey && e.key === 'K')) {
      e.preventDefault();
      setSelectedIndex(
        (prev) => (prev - 1 + filteredItems.length) % filteredItems.length
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selectedItem = filteredItems[selectedIndex];

      if (selectedItem) {
        handleRouteSelect(selectedItem);
      }
    }
  };

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (listItemRefs.current[selectedIndex]) {
      listItemRefs.current[selectedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'auto',
      });
    }
  }, [selectedIndex, filteredItems]);

  return (
    <>
      <ButtonBase
        onClick={handleOpen}
        sx={{
          border: `1px solid ${theme.palette.primary.dark}`,
          color: theme.palette.primary.dark,
          borderRadius: '999px',
          width: '140px',
          py: '4px',
          pr: '12px',
          pl: '8px',
          height: '32px',
        }}
      >
        <Flex
          alignItems="center"
          gap=".25rem"
          justifyContent="space-between"
          width="100%"
        >
          <Flex
            alignItems="center"
            gap=".25rem"
            sx={{
              '& span': {
                fontSize: '11px',
                color: '#BCBCBC',
              },
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <g clipPath="url(#clip0_1389_92)">
                <path
                  d="M19.0358 9.99995C19.0358 14.9903 14.9904 19.0357 10.0001 19.0357C5.00978 19.0357 0.964355 14.9903 0.964355 9.99995C0.964355 5.00966 5.00978 0.964233 10.0001 0.964233C14.9904 0.964233 19.0358 5.00966 19.0358 9.99995ZM9.28578 13.1071C11.3963 13.1071 13.1072 11.3962 13.1072 9.28566C13.1072 7.17515 11.3963 5.46423 9.28578 5.46423C7.17527 5.46423 5.46436 7.17515 5.46436 9.28566C5.46436 11.3962 7.17527 13.1071 9.28578 13.1071Z"
                  fill="#D7E0FF"
                  stroke="#21005D"
                  strokeWidth="0.5"
                />
                <path
                  d="M12.6072 9.28566C12.6072 11.12 11.1202 12.6071 9.28578 12.6071C7.45141 12.6071 5.96436 11.12 5.96436 9.28566C5.96436 7.45129 7.45141 5.96423 9.28578 5.96423C11.1202 5.96423 12.6072 7.45129 12.6072 9.28566Z"
                  fill="white"
                  stroke="#21005D"
                  strokeWidth="0.5"
                />
                <path
                  d="M10.0001 19.2857C15.1285 19.2857 19.2858 15.1284 19.2858 9.99995C19.2858 4.87159 15.1285 0.714233 10.0001 0.714233C4.87171 0.714233 0.714355 4.87159 0.714355 9.99995C0.714355 15.1284 4.87171 19.2857 10.0001 19.2857Z"
                  stroke="#21005D"
                  strokeWidth="0.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14.2859 14.2856L11.8145 11.8142"
                  stroke="#21005D"
                  strokeWidth="0.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_1389_92">
                  <rect width="20" height="20" fill="white" />
                </clipPath>
              </defs>
            </svg>
            <span>Buscar...</span>
          </Flex>
          <Box
            component="span"
            sx={{ fontSize: 11, color: theme.palette.primary.dark }}
          >
            {/Mac|iPhone|iPad|iPod/i.test(navigator.userAgent)
              ? '⌘+K'
              : 'Ctrl+K'}
          </Box>
        </Flex>
      </ButtonBase>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ style: { maxHeight: 420 } }}
      >
        <DialogTitle>Navigation</DialogTitle>
        <DialogContent>
          <TextField
            inputRef={inputRef}
            fullWidth
            placeholder="Search routes..."
            value={filterText}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            onChange={(e) => {
              setFilterText(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleInputKeyDown}
            margin="normal"
          />
          <List sx={{ maxHeight: 300, overflowY: 'auto' }}>
            {filteredItems.map((item, index) => (
              <ListItem
                key={item.path}
                button
                selected={index === selectedIndex}
                onClick={() => handleRouteSelect(item)}
                ref={(el) => {
                  listItemRefs.current[index] = el as unknown as HTMLLIElement;
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.name}
                  secondary={item.description}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
};
